import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError, fileUrl, LMS_KATEGORI } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, Play, ClipboardList, BookOpen, Save,
  Settings, Upload, ArrowLeft, Users,
} from "lucide-react";
import { extractYoutubeId, LmsStatusBadge, LmsEmpty } from "./LmsShared";

/* ============ Daftar Pelatihan ============ */
export function LmsDaftarKepsek() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/lms/pelatihan");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Hapus pelatihan ini? Semua progres guru untuk pelatihan ini juga akan dihapus.")) return;
    try { await api.delete(`/lms/pelatihan/${id}`); toast.success("Terhapus"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout nav={NAV_KEPSEK} title="LMS — Pelatihan Guru">
      <PageTitle
        title="Daftar Pelatihan Guru"
        subtitle="Kelola video pelatihan dan pantau progres guru"
        action={
          <div className="flex flex-wrap gap-2">
            <button onClick={() => nav("/kepsek/lms/pengaturan")} className="pp-btn-secondary" data-testid="lms-settings-btn">
              <Settings className="w-4 h-4" /> Pengaturan TTD & Cap
            </button>
            <button onClick={() => nav("/kepsek/lms/input")} className="pp-btn-primary" data-testid="lms-new-btn">
              <Plus className="w-4 h-4" /> Tambah Pelatihan
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="pp-card p-10 text-center text-slate-400">Memuat…</div>
      ) : items.length === 0 ? (
        <div className="pp-card p-10"><LmsEmpty msg="Belum ada pelatihan. Klik Tambah Pelatihan untuk memulai." /></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="pp-card p-5 flex flex-col" data-testid={`lms-card-${it.id}`}>
              <div className="aspect-video mb-3 rounded-lg overflow-hidden bg-slate-100">
                <img
                  src={`https://img.youtube.com/vi/${it.youtube_id}/mqdefault.jpg`}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-slate-900 line-clamp-2">{it.judul}</h3>
                <div className="flex gap-1 shrink-0">
                  <span className="pp-badge-neutral">{it.kategori || "Umum"}</span>
                  <span className={it.aktif ? "pp-badge-success" : "pp-badge-neutral"}>{it.aktif ? "Aktif" : "Nonaktif"}</span>
                </div>
              </div>
              <p className="text-sm text-slate-600 mt-1 line-clamp-2">{it.deskripsi}</p>
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-3">
                <span><ClipboardList className="w-3.5 h-3.5 inline mr-1" />{it.soal_count} soal</span>
                <span>Min. skor {it.min_skor}</span>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                <button onClick={() => nav(`/kepsek/lms/${it.id}/rekap`)} className="pp-btn-secondary flex-1" data-testid={`lms-rekap-${it.id}`}>
                  <Users className="w-3.5 h-3.5" /> Rekap
                </button>
                <button onClick={() => nav(`/kepsek/lms/${it.id}/edit`)} className="pp-btn-ghost" data-testid={`lms-edit-${it.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => del(it.id)} className="pp-btn-danger" data-testid={`lms-del-${it.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}

/* ============ Input/Edit Pelatihan ============ */
const emptySoal = () => ({ pertanyaan: "", opsi_a: "", opsi_b: "", opsi_c: "", opsi_d: "", jawaban: "a" });

export function LmsFormKepsek({ editMode = false }) {
  const nav = useNavigate();
  const { id } = useParams();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    judul: "", deskripsi: "", youtube_url: "", min_skor: 70, aktif: true, kategori: "Pedagogi",
    soal: [emptySoal()],
  });

  useEffect(() => {
    if (!editMode || !id) return;
    (async () => {
      try {
        const { data } = await api.get(`/lms/pelatihan/${id}`);
        setForm({
          judul: data.judul,
          deskripsi: data.deskripsi || "",
          youtube_url: `https://www.youtube.com/watch?v=${data.youtube_id}`,
          min_skor: data.min_skor,
          aktif: !!data.aktif,
          kategori: data.kategori || "Pedagogi",
          soal: data.soal.map((s) => {
            const o = s.opsi || {};
            return {
              pertanyaan: s.pertanyaan,
              opsi_a: o.a || "", opsi_b: o.b || "", opsi_c: o.c || "", opsi_d: o.d || "",
              jawaban: s.jawaban || "a",
            };
          }),
        });
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [editMode, id]);

  const setSoal = (idx, patch) => setForm((s) => {
    const next = [...s.soal]; next[idx] = { ...next[idx], ...patch };
    return { ...s, soal: next };
  });
  const addSoal = () => setForm((s) => ({ ...s, soal: [...s.soal, emptySoal()] }));
  const delSoal = (idx) => setForm((s) => ({ ...s, soal: s.soal.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    const yid = extractYoutubeId(form.youtube_url);
    if (!yid) return toast.error("Link YouTube tidak valid");
    if (form.soal.length === 0) return toast.error("Minimal 1 soal");
    for (const [i, s] of form.soal.entries()) {
      if (!s.pertanyaan.trim()) return toast.error(`Soal #${i+1}: pertanyaan kosong`);
      if (!s.opsi_a || !s.opsi_b || !s.opsi_c || !s.opsi_d) return toast.error(`Soal #${i+1}: semua opsi wajib`);
      if (!["a","b","c","d"].includes(s.jawaban)) return toast.error(`Soal #${i+1}: kunci jawaban invalid`);
    }
    setSaving(true);
    try {
      const payload = { ...form, min_skor: parseInt(form.min_skor, 10) || 70 };
      if (editMode) await api.put(`/lms/pelatihan/${id}`, payload);
      else await api.post("/lms/pelatihan", payload);
      toast.success(editMode ? "Perubahan disimpan (skor lama guru direset karena bank soal berubah)" : "Pelatihan dibuat");
      nav("/kepsek/lms");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Layout nav={NAV_KEPSEK} title="LMS — Pelatihan Guru">
      <PageTitle
        title={editMode ? "Edit Pelatihan" : "Tambah Pelatihan"}
        action={<button onClick={() => nav("/kepsek/lms")} className="pp-btn-secondary"><ArrowLeft className="w-4 h-4" /> Kembali</button>}
      />
      <form onSubmit={submit} className="pp-card p-6 space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="pp-label">Judul Pelatihan *</label>
            <input className="pp-input" required value={form.judul}
              onChange={(e) => setForm({ ...form, judul: e.target.value })}
              data-testid="lms-judul" />
          </div>
          <div>
            <label className="pp-label">Link / ID YouTube *</label>
            <input className="pp-input" required placeholder="https://youtube.com/watch?v=..."
              value={form.youtube_url}
              onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
              data-testid="lms-yturl" />
            {extractYoutubeId(form.youtube_url) &&
              <p className="mt-1 text-xs text-emerald-600">ID terdeteksi: {extractYoutubeId(form.youtube_url)}</p>}
          </div>
          <div>
            <label className="pp-label">Kategori *</label>
            <select className="pp-input" value={form.kategori}
              onChange={(e) => setForm({ ...form, kategori: e.target.value })}
              data-testid="lms-kategori">
              {LMS_KATEGORI.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="pp-label">Deskripsi</label>
            <textarea className="pp-input min-h-[80px]" value={form.deskripsi}
              onChange={(e) => setForm({ ...form, deskripsi: e.target.value })}
              data-testid="lms-desc" />
          </div>
          <div>
            <label className="pp-label">Nilai Minimal Kelulusan *</label>
            <input type="number" min="0" max="100" className="pp-input" required value={form.min_skor}
              onChange={(e) => setForm({ ...form, min_skor: e.target.value })}
              data-testid="lms-minskor" />
          </div>
          <div>
            <label className="pp-label">Status</label>
            <label className="flex items-center gap-2 pp-input" style={{ background: "white" }}>
              <input type="checkbox" checked={form.aktif}
                onChange={(e) => setForm({ ...form, aktif: e.target.checked })}
                data-testid="lms-aktif" />
              <span>{form.aktif ? "Aktif — tampil untuk guru" : "Nonaktif — disembunyikan"}</span>
            </label>
          </div>
        </div>

        <div className="pt-4 border-t border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Bank Soal (Pilihan Ganda)</h3>
            <button type="button" onClick={addSoal} className="pp-btn-secondary" data-testid="lms-add-soal">
              <Plus className="w-4 h-4" /> Tambah Soal
            </button>
          </div>
          <div className="space-y-4">
            {form.soal.map((s, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-4" data-testid={`lms-soal-${i}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <span className="text-sm font-semibold text-indigo-700">Soal #{i+1}</span>
                  {form.soal.length > 1 &&
                    <button type="button" onClick={() => delSoal(i)} className="pp-btn-danger text-xs">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>}
                </div>
                <textarea className="pp-input mb-3" required rows={2} placeholder="Tulis pertanyaan…"
                  value={s.pertanyaan} onChange={(e) => setSoal(i, { pertanyaan: e.target.value })} />
                <div className="grid sm:grid-cols-2 gap-2">
                  {["a","b","c","d"].map((k) => (
                    <div key={k} className="flex items-center gap-2">
                      <label className="flex items-center gap-1 text-xs font-semibold w-14">
                        <input type="radio" name={`jw-${i}`} checked={s.jawaban === k}
                          onChange={() => setSoal(i, { jawaban: k })} data-testid={`lms-jw-${i}-${k}`} />
                        {k.toUpperCase()}
                      </label>
                      <input className="pp-input flex-1" required placeholder={`Opsi ${k.toUpperCase()}`}
                        value={s[`opsi_${k}`]} onChange={(e) => setSoal(i, { [`opsi_${k}`]: e.target.value })} />
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">Klik radio A/B/C/D untuk menandai jawaban benar.</p>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={() => nav("/kepsek/lms")} className="pp-btn-secondary">Batal</button>
          <button type="submit" disabled={saving} className="pp-btn-primary" data-testid="lms-save">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </form>
    </Layout>
  );
}

/* ============ Rekap Per Pelatihan ============ */
export function LmsRekapKepsek() {
  const nav = useNavigate();
  const { id } = useParams();
  const [pel, setPel] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lms/pelatihan/${id}/rekap`);
        setPel(data.pelatihan); setRows(data.rows);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [id]);

  const dlCert = async (kode) => {
    try {
      const r = await api.get(`/lms/sertifikat/${kode}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `sertifikat-${kode}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout nav={NAV_KEPSEK} title="LMS — Rekap Pelatihan">
      <PageTitle
        title={pel ? `Rekap · ${pel.judul}` : "Rekap"}
        subtitle={pel ? `Minimal skor kelulusan: ${pel.min_skor}` : ""}
        action={<button onClick={() => nav("/kepsek/lms")} className="pp-btn-secondary"><ArrowLeft className="w-4 h-4" /> Kembali</button>}
      />
      <div className="pp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Guru</th>
            <th className="text-left px-5 py-3">NIY</th>
            <th className="text-left px-5 py-3">Status</th>
            <th className="text-right px-5 py-3">Percobaan</th>
            <th className="text-right px-5 py-3">Skor</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Belum ada data.</td></tr>}
            {rows.map((r) => (
              <tr key={r.guru_id} className="pp-row" data-testid={`lms-rekap-row-${r.guru_id}`}>
                <td className="px-5 py-3 font-medium">{r.nama}</td>
                <td className="px-5 py-3 font-mono text-xs">{r.niy || "—"}</td>
                <td className="px-5 py-3"><LmsStatusBadge status={r.status} /></td>
                <td className="px-5 py-3 text-right">{r.attempts || 0}</td>
                <td className="px-5 py-3 text-right font-semibold">{r.skor == null ? "—" : r.skor}</td>
                <td className="px-5 py-3 text-right">
                  {r.kode_sertifikat &&
                    <button onClick={() => dlCert(r.kode_sertifikat)} className="pp-btn-ghost text-xs"
                      data-testid={`lms-dl-cert-${r.guru_id}`}>Unduh Sertifikat</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}

/* ============ Pengaturan TTD & Cap ============ */
export function LmsPengaturanKepsek() {
  const nav = useNavigate();
  const [form, setForm] = useState({ nama_kepsek: "", nip_kepsek: "", ttd_file_id: null, cap_file_id: null });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/lms/settings"); setForm({ ...form, ...data }); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
    // eslint-disable-next-line
  }, []);

  const uploadImg = async (e, field) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("File harus gambar (PNG rekomendasi transparan)"); return; }
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, [field]: data.id }));
      toast.success("Gambar diunggah");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/lms/settings", form); toast.success("Pengaturan tersimpan"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Layout nav={NAV_KEPSEK} title="LMS — Pengaturan">
      <PageTitle
        title="Pengaturan Sertifikat"
        subtitle="Nama Kepala Sekolah, TTD & Cap yang tercetak di sertifikat lulus"
        action={<button onClick={() => nav("/kepsek/lms")} className="pp-btn-secondary"><ArrowLeft className="w-4 h-4" /> Kembali</button>}
      />
      <div className="pp-card p-6 space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="pp-label">Nama Kepala Sekolah</label>
            <input className="pp-input" value={form.nama_kepsek}
              onChange={(e) => setForm({ ...form, nama_kepsek: e.target.value })}
              data-testid="lms-set-nama" />
          </div>
          <div>
            <label className="pp-label">NIP Kepala Sekolah</label>
            <input className="pp-input" value={form.nip_kepsek}
              onChange={(e) => setForm({ ...form, nip_kepsek: e.target.value })}
              data-testid="lms-set-nip" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="pp-label">Tanda Tangan (PNG transparan, rekomendasi)</label>
            <div className="flex items-center gap-3">
              {form.ttd_file_id && <img src={fileUrl(form.ttd_file_id)} alt="TTD" className="h-20 border rounded bg-white p-2" />}
              <label className="pp-btn-secondary cursor-pointer">
                <Upload className="w-4 h-4" /> {form.ttd_file_id ? "Ganti" : "Upload"}
                <input type="file" accept="image/*" onChange={(e) => uploadImg(e, "ttd_file_id")} className="hidden" data-testid="lms-set-ttd" />
              </label>
            </div>
          </div>
          <div>
            <label className="pp-label">Cap / Stempel Sekolah (PNG transparan)</label>
            <div className="flex items-center gap-3">
              {form.cap_file_id && <img src={fileUrl(form.cap_file_id)} alt="Cap" className="h-20 border rounded bg-white p-2" />}
              <label className="pp-btn-secondary cursor-pointer">
                <Upload className="w-4 h-4" /> {form.cap_file_id ? "Ganti" : "Upload"}
                <input type="file" accept="image/*" onChange={(e) => uploadImg(e, "cap_file_id")} className="hidden" data-testid="lms-set-cap" />
              </label>
            </div>
          </div>
        </div>
        <div className="pt-4 border-t border-slate-200 flex justify-end">
          <button onClick={save} disabled={saving} className="pp-btn-primary" data-testid="lms-set-save">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>
    </Layout>
  );
}
